import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import {
  PREFIX_VAULT_RADAR,
  VAULT_RADAR_ADMIN,
  VAULT_RADAR_USERS,
} from 'authz/utils/permission-types/index';
import { BASE_PATH, Configuration } from '@clients/cloud-vault-radar';
import cookieAuthEnabled from 'core/helpers/cookie-auth-enabled';
import { variation } from 'ember-launch-darkly';

const VAULT_RADAR_ADMIN_PERMISSION = `${PREFIX_VAULT_RADAR}.${VAULT_RADAR_USERS}.${VAULT_RADAR_ADMIN}`;

export default class CloudVaultRadarRoute extends Route {
  @service api;
  @service vaultRadarTenant;
  @service flashMessages;
  @service router;
  @service intl;
  @service permissions;

  handleError = (error) => {
    const { project, organization } = this.modelFor('cloud.services');

    if (macroCondition(isDevelopingApp())) {
      //eslint-disable-next-line no-console
      console.error(error);
    }
    this.flashMessages.error(
      this.intl.t('components.page.vault-radar.toast.general.error')
    );
    this.router.transitionTo(
      'cloud.orgs.detail.projects.detail',
      organization.id,
      project.id
    );
  };

  getTenantCreateCall = (organization, project, tenantData) => {
    const tenantExists = tenantData.state && tenantData.type;

    if (tenantExists) {
      return new Promise((resolve) => {
        resolve(tenantData);
      });
    }

    // We only want to call the above creation if the tenantData does not
    // already exist.
    return this.api.vaultRadar.tenant.createTenant(project.id, {
      location: {
        organizationId: organization.id,
      },
    });
  };

  getCanProvisionTenantCall = async () => {
    const { project } = this.modelFor('cloud.services');

    if (!variation('hcp-vault-radar-user-interface-show-billing')) {
      return true;
    }

    // So this is being done explicity to avoid having to publish this endpoint to the Public API pipeline. It's a temp
    // measure until billing can implement the AllowBillableRequest endpoint through their APIs, but Radar currently
    // needs it now, so has self published a way for the client to make that call. However, we don't want to own
    // or maintain this endpoint long term, so DO NOT RE-USE THIS PATTERN.
    const hasCookieAuth = cookieAuthEnabled();
    const credentials = hasCookieAuth
      ? {
          credentials: 'include',
        }
      : {};

    const vaultRadarConfiguration = new Configuration({
      ...this.api.config(BASE_PATH),
      ...credentials,
    });

    const customHeaders = hasCookieAuth
      ? {}
      : {
          Authorization:
            vaultRadarConfiguration.configuration.apiKey('Authorization'),
        };

    try {
      const response = await vaultRadarConfiguration.fetchApi(
        `${vaultRadarConfiguration.configuration.basePath}/2023-05-01/vault-radar/projects/${project?.id}/api/billing/allow-billable-request`,
        {
          ...vaultRadarConfiguration.configuration,
          method: 'POST',
          headers: {
            ...vaultRadarConfiguration.configuration.headers,
            ...customHeaders,
          },
        }
      );
      const data = await response.json();
      return !!data?.allow;
    } catch (error) {
      if (macroCondition(isDevelopingApp())) {
        //eslint-disable-next-line no-console
        console.error(error);
      }
      return false;
    }
  };

  async model() {
    const { project, organization } = this.modelFor('cloud.services');
    const isAdmin = this.permissions.has(VAULT_RADAR_ADMIN_PERMISSION);
    await this.vaultRadarTenant.fetch();

    if (!isAdmin) {
      return {
        tenantData: this.vaultRadarTenant.tenantData,
        project,
        organization,
      };
    }

    // Only create if no tenant data exists
    const tenantPromise = this.vaultRadarTenant.getOrCreateTenant();

    const existingTenantPromise =
      this.api.vaultRadar.tenant.listOrganizationTenants(
        project.id,
        organization.id
      );

    try {
      const resolvedData = await Promise.all([
        tenantPromise,
        existingTenantPromise,
        this.getCanProvisionTenantCall(),
      ]);

      const [tenantData, existingTenantData, canProvisionTenant] = resolvedData;
      const { provisionedTenants } = existingTenantData;
      const castedTenantCount = Number.isFinite(parseInt(provisionedTenants))
        ? parseInt(provisionedTenants)
        : 0;

      const hasExistingTenant = castedTenantCount > 0;

      return {
        tenantData,
        hasExistingTenant,
        project,
        organization,
        canProvisionTenant,
      };
    } catch (e) {
      this.handleError(e);
      return {
        project,
        organization,
      };
    }
  }
}
